import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { storageService } from '../services/storage.service';
import logoImage from '../assets/images/app-logo.png';
import { useLang } from '../contexts/LangContext';
import ModalWindow from '../components/ModalWindow';

interface LoginPageProps {
  onLoginSuccess?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [pinLoginAvailable, setPinLoginAvailable] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(''); // Brief error for form display
  const [errorDetails, setErrorDetails] = useState<any>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const isAuthenticated = await authService.isAuthenticated();
        const unlocked = await storageService.isSessionUnlocked();
        const pinEnabled = await authService.isPinEnabled();

        setPinLoginAvailable(isAuthenticated && pinEnabled);

        if (isAuthenticated && unlocked) {
          navigate('/', { replace: true });
        }
      } catch {
        // ignore
      }
    };

    init();
  }, [navigate]);

  const handleLoginViaPin = async () => {
    // Trigger the global PIN gate by navigating to a protected route.
    // If the user is actually logged out, AppInitializer will keep them on /login.
    try {
      await authService.lock();
    } catch {
      // ignore
    }
    navigate('/', { replace: true });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorDetails(null);

    try {
      await authService.login(formData.username, formData.password);
      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        navigate('/');
      }
    } catch (err: any) {
      const errorMessage = err.message || t('auth_loginError');
      
      // Extract original error if it was wrapped
      const originalError = err.originalError || err;
      
      // Determine error type for better categorization
      let errorType = 'Unknown';
      if (originalError.code === 'ECONNABORTED' || originalError.message?.includes('timeout')) {
        errorType = 'Timeout';
      } else if (originalError.code === 'ERR_NETWORK' || originalError.message?.includes('Network Error')) {
        errorType = 'Network Error';
      } else if (originalError.response) {
        errorType = `HTTP ${originalError.response.status}`;
      } else if (originalError.request) {
        errorType = 'No Response';
      }
      
      // Create short, user-friendly error for form display
      let shortError = t('auth_loginError');
      if (originalError.response?.status === 401) {
        shortError = 'Invalid username or password';
      } else if (originalError.code === 'ERR_NETWORK' || !navigator.onLine) {
        shortError = 'Connection error. Check your internet.';
      } else if (originalError.code === 'ECONNABORTED') {
        shortError = 'Connection timeout. Try again.';
      } else if (originalError.response?.status >= 500) {
        shortError = 'Server error. Try again later.';
      }
      
      setError(shortError); // Brief message for form
      
      // Capture detailed error information for modal
      setErrorDetails({
        message: errorMessage, // Full detailed message
        errorType,
        timestamp: new Date().toISOString(),
        username: formData.username,
        networkOnline: navigator.onLine,
        serverUrl: originalError.config?.url || 'Unknown',
        rawError: originalError.response?.data || originalError.message || originalError.toString(),
        statusCode: originalError.response?.status,
        statusText: originalError.response?.statusText,
        errorCode: originalError.code,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContinueOffline = async () => {
    const confirmed = window.confirm(
      t('auth_offlineWarning')
    );

    if (confirmed) {
      await authService.continueOffline();
      navigate('/');
    }
  };

  return (
    <div className="grow bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center px-scr pt-4 pb-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={logoImage}
            alt={t("appTitle")}
            className="w-16 h-16 mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t('auth_welcomeBack')}
          </h2>
          <p className="text-gray-600 text-sm">
            {t('auth_loginSubtitle')}
          </p>
        </div>

        <>
            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Username */}
              <div>
                <label htmlFor="username" className="label block mb-2">
                  {t('auth_usernameLabel')}
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  required
                  autoComplete="username"
                  className="input-form"
                  placeholder={t('auth_enterUsername')}
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="label block mb-2">
                  {t('auth_passwordLabel')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    required
                    autoComplete="current-password"
                    className="input-form pr-12"
                    placeholder={t('auth_enterPassword')}
                  />
                  {formData.password && (
                    <motion.button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute h-full right-0 top-1/2 text-blue-700/75  px-4 rounded-r-2xl hover:bg-gray-100 transition-colors flex items-center justify-center"
                      style={{ y: '-50%' }}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                        </svg>
                      )}
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <motion.button
                  type="button"
                  onClick={() => setShowErrorModal(true)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm text-left hover:bg-red-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <span>{error}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 flex-shrink-0 ml-2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                    </svg>
                  </div>
                  <div className="text-xs text-red-600 mt-1">{t('auth_tapForDetails')}</div>
                </motion.button>
              )}

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading}
                className="btn-form btn-form-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? t('auth_loggingIn') : t('auth_login')}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  {t('auth_or')}
                </span>
              </div>
            </div>

            {/* Sign Up Link */}
            <button
              onClick={() => navigate('/signup')}
              className="btn-form btn-form-secondary mb-3"
            >
              {t('auth_createNewAccount')}
            </button>

            {/* Login via PIN */}
            {pinLoginAvailable && (
              <button
                onClick={handleLoginViaPin}
                className="btn-form btn-form-secondary mb-3"
              >
                {t('auth_loginViaPin')}
              </button>
            )}

            {/* Continue Offline */}
            <button
              onClick={handleContinueOffline}
              className="w-full text-lg text-gray-600 py-3 rounded-xl font-medium bg-gray-100 hover:bg-gray-200 transition-colors text-sm"
            >
              {t('auth_continueWithoutLogin')}
              <p className="text-xs text-gray-500 text-center leading-tight">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="inline-block mr-2 size-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
              {t('auth_offlineNote')}
            </p>
            </button>            
        </>
      </motion.div>

      {/* Error Details Modal */}
      {showErrorModal && errorDetails && (
        <ModalWindow
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
        >
          <div className="bg-white rounded-2xl shadow-xl p-6 mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-red-600">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  {t('auth_loginErrorTitle')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('auth_loginErrorSubtitle')}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {/* Error Message */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-red-900 mb-1">{t('auth_errorMessage')}</div>
                <div className="text-sm text-red-700">{errorDetails.message}</div>
              </div>

              {/* Error Type */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-gray-700 mb-1">Error Type</div>
                <div className="text-sm text-gray-900 font-mono">{errorDetails.errorType}</div>
              </div>

              {/* Network Status */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-gray-700 mb-1">Network Status</div>
                <div className="text-sm text-gray-900">
                  <span className={`inline-flex items-center gap-1 ${errorDetails.networkOnline ? 'text-green-600' : 'text-red-600'}`}>
                    <span className={`w-2 h-2 rounded-full ${errorDetails.networkOnline ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    {errorDetails.networkOnline ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>

              {/* Username */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-gray-700 mb-1">{t('auth_attemptedUsername')}</div>
                <div className="text-sm text-gray-900 font-mono">{errorDetails.username}</div>
              </div>

              {/* Status Code */}
              {errorDetails.statusCode && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-gray-700 mb-1">{t('auth_statusCode')}</div>
                  <div className="text-sm text-gray-900">
                    {errorDetails.statusCode} {errorDetails.statusText && `- ${errorDetails.statusText}`}
                  </div>
                </div>
              )}

              {/* Timestamp */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-gray-700 mb-1">{t('auth_timestamp')}</div>
                <div className="text-sm text-gray-900">
                  {new Date(errorDetails.timestamp).toLocaleString()}
                </div>
              </div>

              {/* Raw Error (collapsed by default) */}
              <details className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <summary className="text-xs font-semibold text-gray-700 cursor-pointer">
                  {t('auth_technicalDetails')}
                </summary>
                <pre className="text-xs text-gray-600 mt-2 overflow-x-auto">
                  {JSON.stringify(errorDetails.rawError, null, 2)}
                </pre>
              </details>
            </div>

            {/* Suggestions */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm font-semibold text-blue-900 mb-2">{t('auth_suggestions')}</div>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>{t('auth_suggestion1')}</li>
                <li>{t('auth_suggestion2')}</li>
                <li>{t('auth_suggestion3')}</li>
              </ul>
            </div>
          </div>
        </ModalWindow>
      )}
    </div>
  );
};

export default LoginPage;
